import React, { useState } from 'react'
import { graphql, useStaticQuery, Link } from 'gatsby'
import { Helmet } from 'react-helmet'
import {
  SiteMetadataQuery,
  SinglePageQuery,
  Html,
  MdxFrontmatterLinks,
} from 'generated/types/gatsby'
import Header from '../header/Header'
import { Footer } from '../footer/Footer'
import { Scrollbars } from 'react-custom-scrollbars'
import './layout.scss'
import { ContentList } from '../content-list/ContentList'
import Search from '../search/Search'
import PrevAndNext from '../prev-and-next/PrevAndNext'
import { TopNav } from '../top-nav/TopNav'
import { useBreakpoint } from 'gatsby-plugin-breakpoints'

interface LayoutProps {
  readonly children?: React.ReactNode | readonly React.ReactNode[]
  readonly contentNavData: SinglePageQuery['allHtml']['group']
  readonly activeSection: { sectionName: string; chapterName: string; title: string }
  readonly productName: string
  readonly currentDocument:
    | Pick<MdxFrontmatterLinks, 'title' | 'header' | 'path' | 'pdf'>
    | undefined
  readonly title?: string
  readonly scrollTop: number
  readonly prevAndNext: { prev: Html; next: Html }
  readonly searchQuery: string
}

export const Layout = ({
  children,
  contentNavData,
  activeSection,
  productName,
  currentDocument,
  scrollTop,
  prevAndNext,
  searchQuery,
}: LayoutProps) => {
  const data = useStaticQuery<SiteMetadataQuery>(graphql`
    query SiteMetadata {
      site {
        siteMetadata {
          title
          description
        }
      }
    }
  `)
  const { prev, next } = prevAndNext
  const [scrollTopState, setScrollTop] = useState(scrollTop)
  let scrollRef: Scrollbars

  const onScrollStop = () => {
    if (scrollRef === null) {
      return
    }
    const curresntScrollTop = scrollRef.getValues().scrollTop
    if (scrollTopState === curresntScrollTop) {
      return
    }
    setScrollTop(curresntScrollTop)
  }

  const breakpoints = useBreakpoint()
  const [menuState, setMenuState] = useState(false)

  return (
    <main className={`la-page${menuState ? ' la-page_has-active-menu' : ''}`}>
      <Helmet
        titleTemplate={`%s - ${data.site.siteMetadata.title}`}
        defaultTitle={data.site.siteMetadata.title}
        meta={[
          {
            name: 'description',
            content: data.site.siteMetadata.description,
          },
          {
            name: 'keywords',
            content: 'LiveAction, LiveNX, Omnipeek, Documentation',
          },
        ]}
      />

      <Header
        title={data.site.siteMetadata.title}
        toggleMenu={() => {
          setMenuState(menuState === undefined ? true : !menuState)
        }}
      >
        <Search productName={productName} searchQuery={searchQuery} autoHeightMax={'75vh'} />
      </Header>

      {!(breakpoints.sm || (breakpoints.md && breakpoints.portrait)) ? <TopNav /> : null}

      <div className={'la-page-body'}>
        <div className={'la-page-body__nav'}>
          <Link
            to={`/${productName}`}
            className={'la-page-body__nav-breadcrumbs la-icon la-icon_chevron-left'}
          >
            {productName} Documentation
          </Link>

          <div className={'la-page-body__nav-title'}>{currentDocument?.title}</div>

          {contentNavData?.length > 1 || contentNavData?.[0].edges.length > 1 ? (
            <Scrollbars
              className={'la-scrollbar'}
              onScrollStop={onScrollStop}
              ref={(ref: Scrollbars) => {
                if (ref === null) {
                  return
                }
                scrollRef = ref
                if (scrollTopState !== undefined) {
                  ref.scrollTop(scrollTopState)
                }
              }}
            >
              <ContentList
                data={contentNavData}
                activeSection={activeSection}
                scrollTop={scrollTopState}
              />
            </Scrollbars>
          ) : null}
        </div>

        <Scrollbars className="la-page-body__main la-scrollbar">
          <div className="la-page-body__main-content-wrapper">
            {productName && (
              <div className="la-page-body__main-breadcrumbs la-breadcrumbs">
                <span className="la-breadcrumbs-title">
                  {productName}:&nbsp;{currentDocument?.header || currentDocument?.title || ''}
                </span>
                {currentDocument?.pdf && (
                  <a
                    className="la-breadcrumbs-pdf nx-lightweight-btn"
                    href={`/pdf/${currentDocument.pdf}`}
                    download
                  >
                    Download PDF
                  </a>
                )}
              </div>
            )}
            <div className="la-page-body__main-content">
              <div className={'la-content'}>{children}</div>
              <PrevAndNext prev={prev} next={next} scrollTop={scrollTopState} />
            </div>
          </div>
          <Footer />
        </Scrollbars>
      </div>

      <button
        className="la-menu-overlay"
        onClick={() => {
          setMenuState(false)
        }}
      />
    </main>
  )
}
